import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useEditor, EditorContent, Extension } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import * as Y from 'yjs';
import { WebsocketProvider } from 'y-websocket';
import { ySyncPlugin, yCursorPlugin, yUndoPlugin } from 'y-prosemirror';
import axios from 'axios';
import dayjs from 'dayjs';
import {
  ArrowLeft, Bold, Italic, Strikethrough, Heading1, Heading2,
  List, ListOrdered, ImagePlus, Trash2, Send, MessageCircle, Reply,
  CornerDownRight, WifiOff, X, Baseline,
} from 'lucide-react';
import { PAPER_BASE } from '../config';
import { authSelector } from '../../../features/auth/slice';

const COLORS = ['#a78bfa','#f87171','#fb923c','#facc15','#4ade80','#22d3ee','#818cf8','#e879f9'];

function getUserColor(id) {
  return COLORS[id % COLORS.length];
}

const TEXT_COLORS = [
  { label: '기본', color: null },
  { label: '흰색', color: '#ffffff' },
  { label: '빨강', color: '#f87171' },
  { label: '주황', color: '#fb923c' },
  { label: '노랑', color: '#facc15' },
  { label: '초록', color: '#4ade80' },
  { label: '하늘', color: '#22d3ee' },
  { label: '보라', color: '#a78bfa' },
  { label: '핑크', color: '#f4b8b8' },
  { label: '회색', color: '#9ca3af' },
];

function Linkify({ children }) {
  if (!children) return null;
  const urlRegex = /(https?:\/\/[^\s<]+)/g;
  const parts = String(children).split(urlRegex);
  return parts.map((part, i) =>
    urlRegex.test(part)
      ? <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="text-violet-400 hover:underline break-all">{part}</a>
      : part
  );
}

async function uploadImage(file) {
  const fd = new FormData();
  fd.append('image', file);
  const r = await axios.post('/api/paper-task/upload', fd);
  return r.data.url;
}

function Toolbar({ editor, onImageUpload }) {
  const [showColorPicker, setShowColorPicker] = useState(false);
  if (!editor) return null;
  const btn = (active, onClick, Icon, title) => (
    <button onClick={onClick} title={title}
      className={`p-1.5 rounded ${active ? 'bg-violet-400/20 text-violet-400' : 'text-white/40 hover:text-white hover:bg-white/5'}`}>
      <Icon size={16} />
    </button>
  );
  const currentColor = editor.getAttributes('textStyle').color || null;
  return (
    <div className="flex items-center gap-0.5 px-3 py-2 border-b border-white/5 bg-white/[0.02] flex-wrap">
      {btn(editor.isActive('bold'), () => editor.chain().focus().toggleBold().run(), Bold, '굵게')}
      {btn(editor.isActive('italic'), () => editor.chain().focus().toggleItalic().run(), Italic, '기울임')}
      {btn(editor.isActive('strike'), () => editor.chain().focus().toggleStrike().run(), Strikethrough, '취소선')}
      <div className="w-px h-5 bg-white/10 mx-1" />
      {btn(editor.isActive('heading', { level: 1 }), () => editor.chain().focus().toggleHeading({ level: 1 }).run(), Heading1, '제목 1')}
      {btn(editor.isActive('heading', { level: 2 }), () => editor.chain().focus().toggleHeading({ level: 2 }).run(), Heading2, '제목 2')}
      <div className="w-px h-5 bg-white/10 mx-1" />
      {btn(editor.isActive('bulletList'), () => editor.chain().focus().toggleBulletList().run(), List, '글머리 기호')}
      {btn(editor.isActive('orderedList'), () => editor.chain().focus().toggleOrderedList().run(), ListOrdered, '번호 목록')}
      <div className="w-px h-5 bg-white/10 mx-1" />
      {/* 글자색 */}
      <div className="relative">
        <button title="글자색" onClick={() => setShowColorPicker(v => !v)}
          className="p-1.5 rounded text-white/40 hover:text-white hover:bg-white/5 flex flex-col items-center gap-0.5">
          <Baseline size={16} style={{ color: currentColor || undefined }} />
          <div className="w-3.5 h-1 rounded-sm" style={{ background: currentColor || 'rgba(255,255,255,0.2)' }} />
        </button>
        {showColorPicker && (
          <div className="absolute top-full left-0 mt-1 z-50 bg-[#1a1428] border border-white/10 rounded-lg p-2 flex gap-1.5 flex-wrap w-[136px]">
            {TEXT_COLORS.map(({ label, color }) => (
              <button key={label} title={label}
                onClick={() => { color ? editor.chain().focus().setColor(color).run() : editor.chain().focus().unsetColor().run(); setShowColorPicker(false); }}
                className={`w-5 h-5 rounded-full border-2 transition-transform hover:scale-110 ${currentColor === color ? 'border-white' : 'border-transparent'}`}
                style={{ background: color || 'linear-gradient(135deg, #fff 45%, #f00 45%)' }}
              />
            ))}
          </div>
        )}
      </div>
      <div className="w-px h-5 bg-white/10 mx-1" />
      <button onClick={onImageUpload} title="이미지" className="p-1.5 rounded text-white/40 hover:text-white hover:bg-white/5">
        <ImagePlus size={16} />
      </button>
    </div>
  );
}

function CommentItem({ c, onReply, onDelete, replyTo, setReplyTo }) {
  const [replyText, setReplyText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submitReply = async () => {
    if (!replyText.trim() || submitting) return;
    setSubmitting(true);
    await onReply(c.id, replyText);
    setReplyText('');
    setSubmitting(false);
    setReplyTo(null);
  };

  return (
    <div>
      <div className="bg-white/[0.04] rounded-lg px-3 py-2 group">
        <div className="flex items-center justify-between mb-0.5">
          <span className="text-white text-xs font-medium">{c.memberAuthor?.nickName || c.memberAuthor?.name || c.author?.name}</span>
          <div className="flex items-center gap-2">
            <span className="text-white/20 text-xs">{dayjs(c.createdAt).format('MM.DD HH:mm')}</span>
            <button onClick={() => setReplyTo(replyTo === c.id ? null : c.id)}
              className="text-white/20 hover:text-violet-400 opacity-0 group-hover:opacity-100"><Reply size={12} /></button>
            <button onClick={() => onDelete(c.id)}
              className="text-white/20 hover:text-red-400 opacity-0 group-hover:opacity-100"><Trash2 size={12} /></button>
          </div>
        </div>
        <p className="text-white/70 text-sm whitespace-pre-wrap"><Linkify>{c.content}</Linkify></p>
      </div>
      {c.replies?.length > 0 && (
        <div className="ml-5 mt-1 space-y-1">
          {c.replies.map(r => (
            <div key={r.id} className="bg-white/[0.02] rounded-lg px-3 py-2 group flex gap-2">
              <CornerDownRight size={12} className="text-white/20 mt-1 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-white text-xs font-medium">{r.memberAuthor?.nickName || r.memberAuthor?.name || r.author?.name}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-white/20 text-xs">{dayjs(r.createdAt).format('MM.DD HH:mm')}</span>
                    <button onClick={() => onDelete(r.id)}
                      className="text-white/20 hover:text-red-400 opacity-0 group-hover:opacity-100"><Trash2 size={12} /></button>
                  </div>
                </div>
                <p className="text-white/70 text-sm whitespace-pre-wrap"><Linkify>{r.content}</Linkify></p>
              </div>
            </div>
          ))}
        </div>
      )}
      {replyTo === c.id && (
        <div className="ml-5 mt-1 flex gap-2">
          <CornerDownRight size={14} className="text-white/20 mt-2.5 flex-shrink-0" />
          <input value={replyText} onChange={e => setReplyText(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && submitReply()}
            placeholder="답글 입력..." autoFocus
            className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-violet-400" />
          <button onClick={submitReply} disabled={submitting}
            className="px-2 py-1.5 bg-violet-500 text-white rounded-lg hover:bg-violet-400 disabled:opacity-50"><Send size={12} /></button>
        </div>
      )}
    </div>
  );
}

export default function PaperAdminTaskEditorPage() {
  const { boardId, taskId } = useParams();
  const navigate = useNavigate();
  const { me } = useSelector(authSelector.selectAuthState);
  const isSuperAdmin = me?.superAdminYn === 'Y';

  const [task, setTask] = useState(null);
  const [connected, setConnected] = useState(false);
  const [peers, setPeers] = useState([]);
  const peersRef = useRef('[]');
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [comments, setComments] = useState([]);
  const [showComments, setShowComments] = useState(false);
  const fileInputRef = useRef(null);

  const { ydoc, provider } = useMemo(() => {
    const doc = new Y.Doc();
    const token = localStorage.getItem('token');
    const apiUrl = process.env.REACT_APP_API_URL || 'https://api.urpink.co.kr';
    const wsUrl = apiUrl.replace('https://', 'wss://').replace('http://', 'ws://') + '/yjs';
    const prov = new WebsocketProvider(wsUrl, `paper-task-${taskId}`, doc, {
      params: { token },
    });
    return { ydoc: doc, provider: prov };
  }, [taskId]);

  const ytitle = useMemo(() => ydoc.getText('title'), [ydoc]);

  const loadTask = useCallback(() => {
    axios.get(`/api/paper-task/boards/${boardId}/tasks/${taskId}`).then(r => {
      setTask(r.data);
      setComments(r.data.comments || []);
    });
  }, [boardId, taskId]);

  useEffect(() => { loadTask(); }, [loadTask]);

  useEffect(() => {
    provider.on('status', ({ status }) => {
      setConnected(status === 'connected');
    });

    provider.awareness.setLocalStateField('user', {
      name: me?.nickName || me?.name || 'Unknown',
      color: getUserColor(me?.id || 0),
      profileImage: me?.profileImage || null,
    });

    const awarenessHandler = () => {
      const seen = new Set();
      const users = [];
      const myName = me?.nickName || me?.name || 'Unknown';
      provider.awareness.getStates().forEach((state) => {
        if (state.user && !seen.has(state.user.name)) {
          seen.add(state.user.name);
          users.push({ name: state.user.name, color: state.user.color, profileImage: state.user.profileImage, isMe: state.user.name === myName });
        }
      });
      if (!seen.has(myName)) {
        users.unshift({ name: myName, color: getUserColor(me?.id || 0), profileImage: me?.profileImage || null, isMe: true });
      } else {
        const idx = users.findIndex(u => u.isMe);
        if (idx > 0) users.unshift(users.splice(idx, 1)[0]);
      }
      const pStr = JSON.stringify(users);
      if (pStr !== peersRef.current) { peersRef.current = pStr; setPeers(users); }
    };
    provider.awareness.on('change', awarenessHandler);
    awarenessHandler();

    return () => {
      provider.destroy();
      ydoc.destroy();
    };
  }, [provider, ydoc, me]);

  const [title, setTitle] = useState('');
  const titleSaveTimer = useRef(null);
  const initialTitle = useRef(true);

  useEffect(() => {
    const update = () => setTitle(ytitle.toString());
    ytitle.observe(update);
    const onSync = (isSynced) => { if (isSynced) { update(); initialTitle.current = true; } };
    provider.on('synced', onSync);
    if (provider.synced) { update(); initialTitle.current = true; }
    return () => {
      ytitle.unobserve(update);
      provider.off('synced', onSync);
    };
  }, [ytitle, provider]);

  useEffect(() => {
    if (initialTitle.current) { initialTitle.current = false; return; }
    if (titleSaveTimer.current) clearTimeout(titleSaveTimer.current);
    titleSaveTimer.current = setTimeout(() => {
      if (title.trim()) axios.put(`/api/paper-task/boards/${boardId}/tasks/${taskId}`, { title });
    }, 500);
    return () => { if (titleSaveTimer.current) clearTimeout(titleSaveTimer.current); };
  }, [title, boardId, taskId]);

  const handleTitleChange = (e) => {
    const newVal = e.target.value;
    ydoc.transact(() => {
      ytitle.delete(0, ytitle.length);
      if (newVal) ytitle.insert(0, newVal);
    });
  };

  const YjsExtension = useMemo(() => Extension.create({
    name: 'yjs',
    addProseMirrorPlugins() {
      const fragment = ydoc.getXmlFragment('content');
      return [
        ySyncPlugin(fragment),
        yCursorPlugin(provider.awareness),
        yUndoPlugin(),
      ];
    },
  }), [ydoc, provider]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ history: false }),
      YjsExtension,
      Image.configure({ inline: false, allowBase64: false }),
      Placeholder.configure({ placeholder: '내용을 입력하세요...' }),
      TextStyle,
      Color,
    ],
    editorProps: {
      attributes: {
        class: 'prose prose-invert max-w-none focus:outline-none min-h-[400px] px-6 py-4',
      },
      handleDrop: (view, event) => {
        const files = event.dataTransfer?.files;
        if (files?.length) {
          event.preventDefault();
          Array.from(files).forEach(async (f) => {
            if (!f.type.startsWith('image/')) return;
            const url = await uploadImage(f);
            view.dispatch(view.state.tr.replaceSelectionWith(
              view.state.schema.nodes.image.create({ src: url })
            ));
          });
          return true;
        }
        return false;
      },
      handlePaste: (view, event) => {
        const items = event.clipboardData?.items;
        if (!items) return false;
        for (const item of items) {
          if (item.type.startsWith('image/')) {
            event.preventDefault();
            const file = item.getAsFile();
            if (!file) continue;
            (async () => {
              const url = await uploadImage(file);
              view.dispatch(view.state.tr.replaceSelectionWith(
                view.state.schema.nodes.image.create({ src: url })
              ));
            })();
            return true;
          }
        }
        return false;
      },
    },
  }, [ydoc]);

  const handleImageButton = () => fileInputRef.current?.click();
  const handleFileSelect = async (e) => {
    const files = e.target.files;
    if (!files?.length || !editor) return;
    for (const f of files) {
      if (!f.type.startsWith('image/')) continue;
      const url = await uploadImage(f);
      editor.chain().focus().setImage({ src: url }).run();
    }
    e.target.value = '';
  };

  const addComment = async (parentId = null, text = null) => {
    const content = text || comment;
    if (!content.trim() || submitting) return;
    setSubmitting(true);
    await axios.post(`/api/paper-task/boards/${boardId}/tasks/${taskId}/comments`, { content, parentId });
    if (!parentId) setComment('');
    setSubmitting(false);
    loadTask();
  };
  const deleteComment = async (cid) => {
    await axios.delete(`/api/paper-task/boards/${boardId}/tasks/${taskId}/comments/${cid}`);
    loadTask();
  };

  const handleDelete = async () => {
    if (!window.confirm('이 할일을 삭제하시겠습니까?')) return;
    await axios.delete(`/api/paper-task/boards/${boardId}/tasks/${taskId}`);
    navigate(`${PAPER_BASE}/admin/tasks/${boardId}`);
  };

  const totalComments = comments.reduce((sum, c) => sum + 1 + (c.replies?.length || 0), 0);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      {/* 헤더 */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <button onClick={() => navigate(`${PAPER_BASE}/admin/tasks/${boardId}`)} className="p-1.5 text-white/30 hover:text-white rounded-lg hover:bg-white/5 flex-shrink-0">
            <ArrowLeft size={18} />
          </button>
          <input value={title} onChange={handleTitleChange}
            placeholder="제목을 입력하세요"
            className="flex-1 bg-transparent text-white text-lg font-bold focus:outline-none placeholder-white/20 min-w-0" />
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="flex items-center gap-1 mr-2">
            <div className="flex -space-x-1.5">
              {peers.slice(0, 5).map((p, i) => (
                <div key={i} className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white border-2 overflow-hidden ${p.isMe ? 'border-violet-400' : 'border-[#0d0b14]'}`}
                  style={{ backgroundColor: p.profileImage ? 'transparent' : p.color }} title={p.isMe ? `${p.name} (나)` : p.name}>
                  {p.profileImage
                    ? <img src={p.profileImage} alt="" className="w-full h-full object-cover" />
                    : p.name?.charAt(0)}
                </div>
              ))}
              {peers.length > 5 && <span className="text-white/30 text-xs ml-1">+{peers.length - 5}</span>}
            </div>
          </div>
          {!connected && (
            <div className="flex items-center gap-1 text-red-400 text-xs" title="연결 끊김">
              <WifiOff size={14} /> 오프라인
            </div>
          )}
          <button onClick={() => setShowComments(!showComments)}
            className={`flex items-center gap-1 px-2 py-1 rounded-lg text-sm ${showComments ? 'bg-violet-400/20 text-violet-400' : 'text-white/40 hover:text-white hover:bg-white/5'}`}>
            <MessageCircle size={14} />
            {totalComments > 0 && <span className="text-xs">{totalComments}</span>}
          </button>
          {(task?.memberAuthorId === me?.id || task?.authorId === me?.id || isSuperAdmin) && (
            <button onClick={handleDelete} className="p-1.5 text-white/30 hover:text-red-400 rounded-lg hover:bg-white/5">
              <Trash2 size={16} />
            </button>
          )}
        </div>
      </div>

      {task && (
        <div className="text-xs text-white/30 mb-4">
          {task.memberAuthor?.nickName || task.memberAuthor?.name || task.author?.name} · {dayjs(task.createdAt).format('YYYY.MM.DD HH:mm')}
          {task.completed && task.completer && <span className="text-green-400/70"> · {task.completer.name} 완료</span>}
        </div>
      )}

      {/* 에디터 */}
      <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl overflow-hidden mb-4">
        <Toolbar editor={editor} onImageUpload={handleImageButton} />
        <EditorContent editor={editor} />
        <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFileSelect} />
      </div>

      {/* 댓글 */}
      {showComments && (
        <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
            <h3 className="text-white text-sm font-semibold flex items-center gap-1.5">
              <MessageCircle size={14} /> 댓글
              {totalComments > 0 && <span className="text-white/30">({totalComments})</span>}
            </h3>
            <button onClick={() => setShowComments(false)} className="text-white/30 hover:text-white"><X size={14} /></button>
          </div>
          <div className="p-3 space-y-2">
            {comments.map(c => (
              <CommentItem key={c.id} c={c} onReply={addComment} onDelete={deleteComment}
                replyTo={replyTo} setReplyTo={setReplyTo} />
            ))}
            {comments.length === 0 && <p className="text-white/20 text-xs text-center py-4">댓글이 없습니다.</p>}
          </div>
          <div className="p-3 border-t border-white/5 flex gap-2">
            <input value={comment} onChange={e => setComment(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !submitting && addComment()}
              placeholder="댓글 입력..."
              className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-violet-400" />
            <button onClick={() => addComment()} disabled={submitting}
              className="px-2 py-1.5 bg-violet-500 text-white rounded-lg hover:bg-violet-400 disabled:opacity-50">
              <Send size={12} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
